import React from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaTag } from 'react-icons/fa';
import DynamicCard from './dynamic/DynamicCard';
import DynamicTag from './dynamic/DynamicTag';
import DynamicButton from './dynamic/DynamicButton';
import { Project } from '../types';

interface ProjectCardProps {
  project: Project;
  index?: number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index = 0 }) => {
  return (
    <div className="relative h-full" style={{ overflow: 'visible' }}>
    <DynamicCard delay={index * 0.1} className="h-full flex flex-col relative">
      {/* Hover gradient overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary-500/0 to-purple-500/0 group-hover:from-primary-500/10 group-hover:to-purple-500/10 transition-all duration-500 pointer-events-none rounded-xl"
        initial={false}
      />
      
      {/* Animated border on hover */}
      <motion.div
        className="absolute inset-0 border-2 border-transparent group-hover:border-primary-500/20 rounded-xl transition-all duration-500 pointer-events-none"
        initial={false}
      />
      <div className="mb-4 flex-grow relative z-10">
        <motion.div 
          className="flex items-start justify-between mb-3"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex-grow group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">{project.title}</h3>
          {project.category && (
            <span className="ml-2 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs rounded-full font-medium uppercase">
              {project.category}
            </span>
          )}
        </motion.div>
        <motion.p 
          className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {project.description}
        </motion.p>

        {project.technologies && project.technologies.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2 text-sm text-gray-500 dark:text-gray-400">
              <FaTag className="mr-1" size={12} />
              <span>Technologies</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.1, y: -2 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <DynamicTag variant="default" size="sm">
                    {tech}
                  </DynamicTag>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      <motion.div 
        className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 relative z-10"
        style={{ overflow: 'visible' }}
        initial={{ opacity: 0.7 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {project.githubUrl && (
          <div className="relative inline-block" style={{ overflow: 'visible' }}>
            <div className="group/button relative">
              <DynamicButton
                variant="outline"
                size="sm"
                onClick={project.githubPublic ? () => window.open(project.githubUrl, '_blank', 'noopener,noreferrer') : undefined}
                disabled={!project.githubPublic}
                className={`flex items-center ${!project.githubPublic ? 'cursor-not-allowed' : ''}`}
              >
                <FaGithub className="mr-2" size={14} />
                Code
              </DynamicButton>
              {!project.githubPublic && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover/button:opacity-100 group-hover/button:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-[100] shadow-xl" style={{ marginBottom: '8px', left: '50%', transform: 'translateX(-50%)', minWidth: 'max-content' }}>
                  Repository is not publicly available
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                </div>
              )}
            </div>
          </div>
        )}
        {project.liveUrl && (
          <DynamicButton
            variant="primary"
            size="sm"
            onClick={() => window.open(project.liveUrl, '_blank', 'noopener,noreferrer')}
            className="flex items-center"
          >
            <FaExternalLinkAlt className="mr-2" size={14} />
            Live Demo
          </DynamicButton>
        )}
      </motion.div>
    </DynamicCard>
    </div>
  );
};

export default ProjectCard;
